import { createLibraryView } from "../../../../packages/app-ui/js/libraryView.js";
import { formatFullDate } from "../../../../packages/app-ui/js/library.js";

export function createLibraryUi({
  document,
  window,
  jobsStore,
  transcriptUi,
  onUploadRequest,
}) {
  const dummyFiles = jobsStore.files;
  const numberFormatter = new Intl.NumberFormat("en-US");
  const libraryRefs = {};
  const CONTENT_SEARCH_MAX_FILES = 20;

  // prefetch state - antifragile design
  const prefetchInFlight = new Set();
  let currentHoveredRow = null;

  function loadTranscriptContent(name) {
    return transcriptUi.loadTranscriptContent(name);
  }

  // prefetch transcript on hover - idempotent, only fires once per row
  function prefetchTranscript(name) {
    if (!name || prefetchInFlight.has(name)) return;
    const file = jobsStore.findByName(name);
    if (!file || file.status !== "complete") return;
    prefetchInFlight.add(name);
    transcriptUi.loadTranscriptContent(name).finally(() => {
      prefetchInFlight.delete(name);
    });
  }

  // set up hover prefetch with proper event handling
  // called after renderFileList() since #fileList is dynamically created
  let hoverPrefetchInstalled = false;
  function setupHoverPrefetch() {
    if (hoverPrefetchInstalled) return;
    const fileList = document.getElementById("fileList");
    if (!fileList) return;

    hoverPrefetchInstalled = true;

    // use mouseover + row tracking to avoid multiple triggers
    fileList.addEventListener("mouseover", (event) => {
      const row = event.target.closest(".file-row[data-filename]");
      if (!row || row === currentHoveredRow) return;
      currentHoveredRow = row;
      prefetchTranscript(row.dataset.filename);
    });

    // clear tracking when leaving file list entirely
    fileList.addEventListener("mouseleave", () => {
      currentHoveredRow = null;
    });
  }

  function getDisplayName(name) {
    const file = jobsStore.findByName(name);
    if (!file) {
      return name;
    }
    if (file.displayName) {
      return file.displayName;
    }
    return file.jobId || name;
  }

  const libraryModule = createLibraryView({
    document,
    window,
    dummyFiles,
    numberFormatter,
    contentSearchMinLength: 2,
    contentSearchMaxFiles: CONTENT_SEARCH_MAX_FILES,
    loadTranscriptContent,
    getDisplayName,
    hideTranscriptView: () => transcriptUi.hideTranscriptView(),
    updateDemoButtons: () => {},
    applyFullLibrary: () => {},
    viewTranscript: (name) => transcriptUi.viewTranscript(name),
    getTranscriptSearchHotkeys: () => transcriptUi.getTranscriptSearchHotkeys(),
  });

  Object.assign(libraryRefs, libraryModule);

  function bindControls({ refreshButton } = {}) {
    if (refreshButton) {
      refreshButton.addEventListener("click", () => {
        if (typeof onUploadRequest === "function") {
          onUploadRequest({ type: "refresh" });
        }
      });
    }
  }

  function updateUsageBanner(quotaBanner, usedMinutes, includedMinutes) {
    if (!quotaBanner) return;
    if (!Number.isFinite(usedMinutes) || usedMinutes < 0) {
      quotaBanner.textContent = "";
      quotaBanner.style.display = "none";
      return;
    }
    const included = Number.isFinite(includedMinutes) ? includedMinutes : 720;
    quotaBanner.style.display = "block";
    quotaBanner.textContent = `${usedMinutes} / ${included} minutes used this month`;
  }

  // wrap renderFileList to install hover prefetch after DOM is ready
  const originalRenderFileList = libraryModule.renderFileList;
  function renderFileList(...args) {
    const result = originalRenderFileList(...args);
    setupHoverPrefetch();
    return result;
  }

  return {
    ...libraryModule,
    renderFileList,
    bindControls,
    updateUsageBanner,
    formatFullDate,
  };
}
